import { PaymentMethod } from '@stripe/stripe-js';

export type PlanId = 'trial' | 'pro' | 'growth' | 'enterprise';

export type CurrencyCode = 'USD' | 'BRL';

export const CURRENCY_OPTIONS: { code: CurrencyCode; flag: string; labelKey: string }[] = [
  { code: 'USD', flag: '\u{1F1FA}\u{1F1F8}', labelKey: 'billing.currency_usd' },
  { code: 'BRL', flag: '\u{1F1E7}\u{1F1F7}', labelKey: 'billing.currency_brl' },
];

export type Subscription = {
  id: string;
  cancel_at_period_end: boolean;
  cancel_at: number | null;
  default_payment_method: string | null;
  current_period_end: number;
  current_period_start: number;
  status: 'active' | 'canceled';
  collection_method: string;
  days_until_due: number | null;
  quantity: number | null;
  items: {
    quantity: number;
    price: { id: string; unit_amount: number; currency: string; nickname: string };
  }[];
};

export type Plan = {
  enabled: boolean;
  features: string[];
  max_desks: number;
  max_brains: number;
  max_integrations: number;
  max_seats: number;
  max_sessions: number;
  name: string;
  description: string;
  plan_id: PlanId;
  created: string;
  updated: string;
};

export type PlansResponse = { plans: Plan[] };

// The billing endpoints proxy to orchestrator, which pins its own Stripe API
// version. These mirror the shapes it returns, so this client does not move
// when either side bumps the Stripe package.
export type Address = {
  city: string | null;
  country: string | null;
  line1: string | null;
  line2: string | null;
  postal_code: string | null;
  state: string | null;
};

export type Discount = { id: string; coupon: Coupon };

export type TaxIdVerification = {
  status: 'pending' | 'unavailable' | 'unverified' | 'verified';
  verified_address: string | null;
  verified_name: string | null;
};

export type TaxId = {
  id: string;
  type: string;
  country: string | null;
  created: number;
  value: string;
  verification: TaxIdVerification | null;
};

export type CustomerInvoiceSettings = {
  custom_fields: Array<{ name: string; value: string }> | null;
  default_payment_method: string | null;
  footer: string | null;
  rendering_options: { amount_tax_display: string | null; template: string | null } | null;
};

export type Customer = {
  id: string;
  address?: Address | null;
  metadata: Record<string, string>;
  balance: number;
  created: number;
  currency?: string | null;
  discount?: Discount | null;
  email: string | null;
  name?: string | null;
  phone?: string | null;
  tax_exempt?: 'exempt' | 'none' | 'reverse' | null;
  invoice_settings: CustomerInvoiceSettings;
  invoice_prefix?: string | null;
  preferred_locales?: string[] | null;
};

export type CustomerResponse = Customer;

export type PaymentMethodsResponse = { payment_methods: PaymentMethod[] };

export type BillingState = {
  current_period_ends: string;
  next_payment: string;
  days_left: number;
  has_card: boolean;
  has_expired: boolean;
  is_canceled: boolean;
  is_expiring: boolean;
  plan_id: PlanId;
  plan_name: string;
  plan_type: 'trial' | 'custom' | 'stripe' | 'google';
  stripe_account_id?: string;
};

export type Coupon = {
  id: string;
  amount_off: number | null;
  percent_off: number | null;
  name: string | null;
  object: 'coupon';
};

export type Invoice = {
  invoice_id: string;
  status: 'paid' | 'open' | 'draft' | 'uncollectible' | 'void';
  paid: boolean;
  paid_at: number | null;
  currency: string;
  invoice_pdf?: string;
  discount?: { coupon: Coupon };
  hosted_invoice_url?: string;
  total: number;
  tax: number;
  subtotal_excluding_tax: number;
  amount_due: number;
  default_tax_rates?: Array<{ percentage: number; display_name: string; jurisdiction: string }>;
  total_discount_amounts?: Array<{ amount: number }>;
  period_start: number;
  period_end: number;
  lines: Array<{
    id: string;
    amount: number;
    quantity: number;
    proration: boolean;
    period: { start: number; end: number };
    price: {
      id: string;
      unit_amount: number | null;
      currency: string;
      nickname: string;
      tiers_mode: string | null;
      tiers?: Array<{ unit_amount: number; up_to: number | null }>;
    };
  }>;
};

export type InvoicesResponse = { invoices?: Invoice[] };

export interface ModalPlanData {
  title: string;
  subtitle?: string;
  body?: Array<{ text: string; number?: string; secondaryText?: string }>;
  plan_id: PlanId;
}
